/**
 * client-key (frontend) - the health plan a record belongs to. Mirror of
 * api/src/lib/client-key.ts; keep the two in sync.
 *
 * The bench never CHOOSES a client. There is no dropdown, by design: a dropdown would be the only
 * thing deciding which health plan a real clinical result is filed under. The value arrives from
 * the server, already derived, and this module only decides how to show it.
 */

export const CLIENT_KEYS = ['alignment', 'zing', 'guidehealth', 'simpra', 'clover', 'bcbsm'] as const;
export type ClientKey = (typeof CLIENT_KEYS)[number];

export const CLIENT_LABELS: Record<ClientKey, string> = {
  alignment: 'Alignment',
  zing: 'Zing',
  guidehealth: 'Guidehealth',
  simpra: 'Simpra',
  clover: 'Clover',
  bcbsm: 'BCBSM',
};

export function isClientKey(v: unknown): v is ClientKey {
  return typeof v === 'string' && (CLIENT_KEYS as readonly string[]).includes(v);
}

/** Display label, or 'Unknown plan' for a record written before the field existed. */
export function clientLabel(v: unknown): string {
  return isClientKey(v) ? CLIENT_LABELS[v] : 'Unknown plan';
}

/**
 * Sort key that groups records by plan. Records with no plan sort last, so a legacy record is
 * visible at the end of the list rather than hidden or mixed into a real plan's group.
 */
export function clientSortKey(v: unknown): string {
  return isClientKey(v) ? `0${CLIENT_LABELS[v]}` : '1~';
}

/**
 * Campaign slug -> plan. A compiled-in MIRROR of `_shared/campaign-clients.json` (the canonical map)
 * and of `api/src/lib/client-key.ts`; `tests/unit/client-map-mirror.test.ts` asserts all three equal,
 * so drift fails the gate rather than reaching a bench.
 *
 * The bench uses this for ONE thing: inverting it, to answer which of the campaigns this station is
 * configured to serve belong to the plan a Hub `?client=` link asked for (see client-preselect.ts).
 * It never decides which plan a record is filed under; that stays server-side, derived from the
 * campaign, fail-closed.
 */
export const CAMPAIGN_CLIENT: Readonly<Record<string, ClientKey>> = {
  '26fobt': 'alignment',
  '25fobt': 'alignment',
  'fobt': 'alignment',
  '25a1c': 'alignment',
  '25ked': 'alignment',
  '25a1cked': 'alignment',
  '25bpcuffs': 'alignment',
  '26a1ckedgen': 'alignment',
  '26a1ckedim': 'alignment',
  'zingfobt': 'zing',
  'guidehealthbp': 'guidehealth',
  'simprafobt': 'simpra',
  'cloverked': 'clover',
  'bcbsmked': 'bcbsm',
};

/** The plan that owns a campaign slug, or null when the map does not know it. */
export function clientForCampaign(campaign: string): ClientKey | null {
  return CAMPAIGN_CLIENT[String(campaign ?? '').trim().toLowerCase()] ?? null;
}
